<!--
  ChartExport Component

  Provides export functionality for ECharts visualizations
  Supports PNG and SVG export formats
-->
<template>
  <div class="chart-export-controls">
    <v-btn-group
      variant="outlined"
      density="compact"
    >
      <AtlasButton
        size="sm"
        variant="secondary"
        icon="mdi-image"
        :loading="exporting === 'png'"
        :disabled="!chartInstance || exporting !== null"
        @click="handleExportPNG"
      >
        PNG
      </AtlasButton>
      <AtlasButton
        size="sm"
        variant="secondary"
        icon="mdi-vector-polyline"
        :loading="exporting === 'svg'"
        :disabled="!chartInstance || exporting !== null"
        @click="handleExportSVG"
      >
        SVG
      </AtlasButton>
    </v-btn-group>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import * as echarts from 'echarts/core'
import type { EChartsType } from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { logger } from '@/utils/logger'
import { AtlasButton } from '@/components/ui'
import { CHART_SURFACE } from '@/ui/chart-config'

echarts.use([SVGRenderer])

/**
 * Props
 */
const props = defineProps<{
  chartInstance: EChartsType | null
  filename?: string
}>()

/**
 * Emits
 */
const emit = defineEmits<{
  'export-start': [format: 'png' | 'svg']
  'export-success': [format: 'png' | 'svg', filename: string]
  'export-error': [format: 'png' | 'svg', error: Error]
}>()

/**
 * Export state
 */
const exporting = ref<'png' | 'svg' | null>(null)

/**
 * Export chart as PNG
 * Uses ECharts getDataURL to generate PNG data URL
 */
async function handleExportPNG() {
  if (!props.chartInstance) return

  exporting.value = 'png'
  emit('export-start', 'png')

  try {
    // Get PNG data URL from ECharts
    const dataURL = props.chartInstance.getDataURL({
      type: 'png',
      pixelRatio: 2, // Higher resolution for better quality
      backgroundColor: CHART_SURFACE,
    })

    // Trigger download
    const filename = props.filename || `chart-${Date.now()}`
    downloadDataURL(dataURL, `${filename}.png`)

    emit('export-success', 'png', `${filename}.png`)
  } catch (error) {
    logger.error('ChartExport', 'PNG export failed', error)
    emit('export-error', 'png', error as Error)
  } finally {
    exporting.value = null
  }
}

/**
 * Export chart as SVG
 * Uses ECharts SVG renderer to generate SVG string
 */
async function handleExportSVG() {
  if (!props.chartInstance) return

  exporting.value = 'svg'
  emit('export-start', 'svg')

  try {
    const svgString = buildSVGString(props.chartInstance)

    if (!svgString) {
      throw new Error('Unable to render chart as SVG')
    }

    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    const filename = props.filename || `chart-${Date.now()}`
    downloadURL(url, `${filename}.svg`)

    URL.revokeObjectURL(url)
    emit('export-success', 'svg', `${filename}.svg`)
  } catch (error) {
    logger.error('ChartExport', 'SVG export failed', error)
    emit('export-error', 'svg', error as Error)
  } finally {
    exporting.value = null
  }
}

function buildSVGString(chart: EChartsType): string | undefined {
  try {
    const direct = chart.renderToSVGString?.()
    if (direct) return direct
  } catch {
    // canvas renderer cannot render to string; fall through
  }

  return renderOffscreenSVG(chart)
}

function renderOffscreenSVG(chart: EChartsType): string | undefined {
  const offscreen = echarts.init(document.createElement('div'), null, {
    renderer: 'svg',
    ssr: true,
    width: chart.getWidth() || 800,
    height: chart.getHeight() || 600,
  })

  try {
    offscreen.setOption({ ...chart.getOption(), backgroundColor: '#ffffff' })
    return offscreen.renderToSVGString()
  } finally {
    offscreen.dispose()
  }
}

/**
 * Download data URL as file
 * Creates temporary anchor element to trigger download
 */
function downloadDataURL(dataURL: string, filename: string) {
  const link = document.createElement('a')
  link.href = dataURL
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Download URL as file
 * Creates temporary anchor element to trigger download
 */
function downloadURL(url: string, filename: string) {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style scoped>
.chart-export-controls {
  display: inline-flex;
  align-items: center;
}
</style>
